import { useEffect, useRef, useState } from 'react';
import './App.css';
import TerminalFrame from './components/TerminalFrame';
import Sidebar from './components/Sidebar';
import StatusBar from './components/StatusBar';
import CommandPalette from './components/CommandPalette';
import MobileTopBar from './components/MobileTopBar';
import PeekSearch from './components/PeekSearch';
import { PeekProvider, usePeek } from './context/PeekContext';
import AboutMe from './components/sections/AboutMe';
import Work from './components/sections/Work';
import Illustrations from './components/sections/Illustrations';
import Experience from './components/sections/Experience';
import BehindThePixels from './components/sections/BehindThePixels';
import Resume from './components/sections/Resume';
import Contact from './components/sections/Contact';
import { navItems } from './data/mock';

const SECTION_MAP = {
  about: AboutMe,
  work: Work,
  illustrations: Illustrations,
  experience: Experience,
  pixels: BehindThePixels,
  resume: Resume,
  contact: Contact
};

// Registers the main element as the peek scope, and resets any open Find bar
// whenever the active section changes.
function PeekScope({ active, children }) {
  const mainRef = useRef(null);
  const { setScope, reset, setOpen } = usePeek();
  const prevActiveRef = useRef(active);

  useEffect(() => {
    setScope(mainRef.current);
  }, [setScope]);

  useEffect(() => {
    if (prevActiveRef.current !== active) {
      // Section changed — close and clear any open Find state so highlight
      // logic doesn't touch stale DOM.
      setOpen(false);
      reset();
      prevActiveRef.current = active;
    }
  }, [active, reset, setOpen]);

  return (
    <main ref={mainRef} className="flex-1 min-w-0 xl:h-full xl:overflow-hidden" data-peek-scope>
      {children}
    </main>
  );
}

function App() {
  const [active, setActive] = useState('about');
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Reset scroll position whenever the active section changes so users always
  // start reading from the top of the new section.
  useEffect(() => {
    // Reset both the window scroll (mobile / <xl viewports) and any scrollable
    // article/main containers.
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.querySelectorAll('.js-section-scroll, main article, main .overflow-y-auto').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [active]);

  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement && document.activeElement.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA';

      // Cmd/Ctrl + K — open command palette
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }

      if (inField) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Toggle sidebar
      if (e.key === '[' || e.key === ']') {
        setCollapsed((v) => !v);
        return;
      }

      // Number keys 1-6 to jump sections
      const n = parseInt(e.key, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= navItems.length) {
        e.preventDefault();
        setActive(navItems[n - 1].key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const Section = SECTION_MAP[active] || AboutMe;

  return (
    <PeekProvider>
      <div className="App min-h-screen">
        <TerminalFrame>
          <div className="flex flex-col h-auto md:h-full min-h-0">
            {/* Mobile only: top bar with hamburger */}
            <MobileTopBar
              active={active}
              onOpenSidebar={() => setMobileNavOpen(true)}
              onOpenPalette={() => setPaletteOpen(true)}
            />

            <div className="flex flex-1 min-h-0">
              <Sidebar
                active={active}
                onNavigate={setActive}
                collapsed={collapsed}
                onToggle={() => setCollapsed((v) => !v)}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
              />
              <PeekScope active={active}>
                <div key={active} className="h-full w-full section-fade">
                  <Section onNavigate={setActive} />
                </div>
              </PeekScope>
            </div>
            <StatusBar onOpenPalette={() => setPaletteOpen(true)} />
          </div>
        </TerminalFrame>
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onNavigate={setActive}
          onToggleSidebar={() => setCollapsed((v) => !v)}
        />
        <PeekSearch />
      </div>
    </PeekProvider>
  );
}

export default App;
